import React, { useRef, useEffect, useState } from "react";
import ConsultationForm from "./Consultation";
import SEO from "../components/SEO";
import heroImg from "../assets/images/PlumbingElectrical/hero.jpeg";
import cap1 from "../assets/images/PlumbingElectrical/cap-1.jpeg";
import cap2 from "../assets/images/PlumbingElectrical/cap-2.jpeg";
import cap3 from "../assets/images/PlumbingElectrical/cap-3.jpeg";
import cap4 from "../assets/images/PlumbingElectrical/cap-4.jpeg";
import cap5 from "../assets/images/PlumbingElectrical/cap-5.jpeg";
import cap6 from "../assets/images/PlumbingElectrical/cap-6.jpeg";
import cap7 from "../assets/images/PlumbingElectrical/cap-7.jpeg";
import cap8 from "../assets/images/PlumbingElectrical/cap-8.jpeg";
import cap9 from "../assets/images/PlumbingElectrical/cap-9.jpeg";

/* ============================================================
   Plumbing, Electrical (LT & HT) — clean magazine-style layout.
   Sections: hero banner, capability card grid, specs band. Hero CTA opens the consultation modal popup.
============================================================= */

const service = {
  title: "Plumbing, Electrical (LT & HT)",
  tagline: "Power and water that never fail a patient floor",
  description: "Low-tension and high-tension electrical systems plus medical-grade plumbing, designed with the redundancy hospitals require — isolated power, emergency generation, and code-compliant drainage.",
};

const capabilities = [
  {
    tag: "Expertise",
    title: "HT/LT distribution",
    desc: "Substation design, transformer sizing, and panel distribution for full-load hospital demand.",
    img: cap1,
  },
  {
    tag: "Expertise",
    title: "Emergency power",
    desc: "Generator backup and UPS systems for critical care, OT, and life-safety loads.",
    img: cap2,
  },
  {
    tag: "Engineering",
    title: "Isolated power systems",
    desc: "IPS for operating rooms and critical care per NFPA 99 wet-location requirements.",
    img: cap3,
  },
  {
    tag: "Expertise",
    title: "Medical plumbing",
    desc: "Domestic water, RO/DI systems, and compliant drainage for clinical areas.",
    img: cap4,
  },
  {
    tag: "Design",
    title: "Lighting design",
    desc: "Task and ambient lighting calibrated for clinical accuracy and patient comfort.",
    img: cap5,
  },
  {
    tag: "Expertise",
    title: "Earthing & lightning protection",
    desc: "Equipotential bonding and lightning arrestor systems per electrical safety code.",
    img: cap6,
  },
  {
    tag: "Engineering",
    title: "Hot water systems",
    desc: "Centralized hot water generation sized for ward, kitchen, and laundry demand.",
    img: cap7,
  },
  {
    tag: "Expertise",
    title: "Drainage & sewage treatment",
    desc: "On-site STP design where municipal capacity requires supplementing.",
    img: cap8,
  },
  {
    tag: "Integration",
    title: "Energy monitoring",
    desc: "Sub-metering and load monitoring for ongoing facility energy management.",
    img: cap9,
  },
];

const specs = [
  { label: "Power redundancy", value: "N+1 generator, dual utility feed" },
  { label: "Code reference", value: "NFPA 99 / NEC Article 517" },
  { label: "IPS coverage", value: "All wet-procedure locations" },
];

function useReveal() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Reveal({ children, className }) {
  const [ref, inView] = useReveal();
  return (
    <div ref={ref} className={`${className || ""} ac-reveal${inView ? " ac-in" : ""}`}>
      {children}
    </div>
  );
}

export default function PlumbingElectrical() {
  const [showConsultation, setShowConsultation] = useState(false);

  useEffect(() => {
    document.body.style.overflow = showConsultation ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showConsultation]);

  return (
    <>
    <SEO/>
    <div className="ac-page">
      <style>{`
        .ac-page { background: #fff; color: #343F86; padding-top: 68px; overflow-x: hidden; }
        .ac-page * { box-sizing: border-box; }
        .ac-inner { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

        .ac-reveal { opacity: 0; transform: translateY(20px); transition: opacity .6s ease, transform .6s ease; }
        .ac-reveal.ac-in { opacity: 1; transform: translateY(0); }

        /* Hero */
        .ac-hero { position: relative; min-height: 420px; display: flex; align-items: center; }
        .ac-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .ac-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to right, rgba(10,10,10,.78) 0%, rgba(10,10,10,.45) 60%, rgba(10,10,10,.25) 100%); }
        .ac-hero-content { position: relative; color: #fff; max-width: 620px; padding: 72px 0; }
        .ac-crumb { font-size: 13px; color: rgba(255,255,255,.75); margin-bottom: 16px; }
        .ac-hero h1 { font-size: 40px; font-weight: 700; line-height: 1.15; margin: 0 0 14px; }
        .ac-hero p { font-size: 16px; line-height: 1.65; color: rgba(255,255,255,.9); margin: 0 0 28px; }
        .ac-cta { display: inline-block; background: #fff; color: #343F86; font-size: 14px; font-weight: 600; padding: 13px 28px; border-radius: 6px; border: none; cursor: pointer; transition: background .2s ease; }
        .ac-cta:hover { background: #E1E3EE; }

        /* Section headings */
        .ac-section { padding: 64px 0; }
        .ac-section-title { font-size: 28px; font-weight: 700; margin: 0 0 8px; text-align: center; }
        .ac-section-sub { font-size: 15px; color: #5B6072; text-align: center; max-width: 560px; margin: 0 auto 40px; line-height: 1.6; }

        /* Capability cards */
        .ac-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
        .ac-card { background: #fff; border: 1px solid #E1E3EE; border-radius: 10px; overflow: hidden; transition: box-shadow .25s ease, transform .25s ease; }
        .ac-card:hover { box-shadow: 0 12px 28px rgba(0,0,0,.1); transform: translateY(-4px); }
        .ac-card-img { width: 100%; height: 200px; object-fit: cover; display: block; }
        .ac-card-body { padding: 18px 20px 22px; }
        .ac-pill { display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: #2F8A98; background: #ECEEF6; padding: 4px 12px; border-radius: 999px; margin-bottom: 10px; }
        .ac-card h3 { font-size: 17px; font-weight: 600; margin: 0 0 8px; }
        .ac-card p { font-size: 13.5px; color: #5B6072; line-height: 1.6; margin: 0; }

        /* Specs band */
        .ac-specs { background: #ECEEF6; }
        .ac-specs-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; text-align: center; }
        .ac-spec-label { font-size: 12px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: #2F8A98; margin-bottom: 8px; }
        .ac-spec-value { font-size: 20px; font-weight: 700; color: #343F86; }

        @media (max-width: 900px) {
          .ac-grid { grid-template-columns: repeat(2, 1fr); }
          .ac-hero h1 { font-size: 32px; }
        }
        @media (max-width: 600px) {
          .ac-grid, .ac-specs-row { grid-template-columns: 1fr; }
          .ac-hero h1 { font-size: 26px; }
          .ac-section { padding: 44px 0; }
        }
      `}</style>

      {/* Hero */}
      <section className="ac-hero">
        <img className="ac-hero-img" src={heroImg} alt={service.title} />
        <div className="ac-hero-overlay" />
        <div className="ac-inner" style={{ width: "100%" }}>
          <div className="ac-hero-content">
            <div className="ac-crumb">Capabilities / {service.title}</div>
            <h1>{service.title}</h1>
            <p>{service.description}</p>
            <button className="ac-cta" onClick={() => setShowConsultation(true)}>
              Get Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="ac-section">
        <div className="ac-inner">
          <Reveal>
            <h2 className="ac-section-title">What We Do</h2>
            <p className="ac-section-sub">{service.tagline} — every discipline needed to take a healthcare facility from concept to commissioning.</p>
          </Reveal>
          <div className="ac-grid">
            {capabilities.map((c) => (
              <Reveal key={c.title}>
                <div className="ac-card">
                  <img className="ac-card-img" src={c.img} alt={c.title} loading="lazy" />
                  <div className="ac-card-body">
                    <span className="ac-pill">{c.tag}</span>
                    <h3>{c.title}</h3>
                    <p>{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Specs band */}
      <section className="ac-section ac-specs">
        <div className="ac-inner">
          <Reveal>
            <div className="ac-specs-row">
              {specs.map((sp) => (
                <div key={sp.label}>
                  <div className="ac-spec-label">{sp.label}</div>
                  <div className="ac-spec-value">{sp.value}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Consultation Modal — same popup as the Navbar's Free Consultation button */}
      {showConsultation && (
        <div
          className="tevah-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="tevah-modal-inner">
            <button
              className="tevah-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm />
          </div>
        </div>
      )}
    </div>
    </>
  );
}
